/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the invitation page component
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import { Grid, GridProps, Paper, styled } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { ITrial } from '../../TrialsArea';
import MenuItemsProvider from '../../providers/tiles/MenuItemsProvider';
import TileExpanded from './TileExpanded';
import TileHeader from './TileHeader';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const FavouriteTile = styled(Paper)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
}))


/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const FavouritesProps: GridProps = {
    //minWidth: `calc(${(100/numberOfColumns).toFixed(5)}% - ${Math.floor(((numberOfColumns - 1) * 16) / numberOfColumns)}px)`,
    //maxWidth: `calc(${(100/numberOfColumns).toFixed(5)}% - ${Math.floor(((numberOfColumns - 1) * 16) / numberOfColumns)}px)`,
    sx: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        '& > .MuiPaper-root': {
            flex: '1 1 auto'
        }
    }
}


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

interface ITrialTileProps {
    trial: ITrial;
    favourite?: boolean;
    toggleFavourite: (id: number) => void;
    toggleExpandedMenuItem?: (trialId: number, expandMenuItemId: number) => void;
    onExpand?: () => void;
    // changeInstitute: (trialId: number, instId: number) => void;
}


const TrialTile: React.FunctionComponent<ITrialTileProps> = ({
    trial,
    favourite,
    toggleFavourite,
    onExpand,
    toggleExpandedMenuItem
    // changeInstitute
}) => {

    const [expanded, setExpanded] = useState(favourite ?? false);

    return (
        <>
            {
                expanded === true ?
                <>
                    <MenuItemsProvider trialId={trial.id}>
                            <TileExpanded 
                                trial={trial}
                                favourite={favourite}
                                expanded={expanded}
                                toggleExpanded={setExpanded}
                                toggleFavourite={toggleFavourite}
                                toggleExpandedMenuItem={toggleExpandedMenuItem}
                            />
                    </MenuItemsProvider>
                </>
                :
                <>
                    <Grid
                        {...FavouritesProps}
                        item
                    >
                        <FavouriteTile
                        >
                            <TileHeader 
                                trialId={trial.id}
                                trialName={trial.name} 
                                protocolId={trial.protocolId}
                                logoLightUrl={trial.logo?.light}
                                logoDarkUrl={trial.logo?.dark}
                                logoDesktopHeight={trial.logo?.desktopHeight}
                                logoDesktopWidth={trial.logo?.desktopWidth}
                                logoMobileHeight={trial.logo?.mobileHeight}
                                logoMobileWidth={trial.logo?.mobileWidth}
                                favourite={favourite ?? false}
                                expanded={expanded}
                                toggle={favourite ? undefined : () => {
                                    setExpanded(c => !c)
                                    if (onExpand) {
                                        setTimeout(() => onExpand(), 0);
                                    }
                                }}
                                toggleFavourite={toggleFavourite}
                            />
                        </FavouriteTile>
            
                    </Grid>
                </>
            }
        
        </>
    );

    
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default TrialTile;